import { useEffect, useMemo } from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  generatePath,
} from 'react-router-dom';
import {
  GeneralLedgerSidepanel,
  PermissionEnum,
  TransactionTypeEnum,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearTransactionState } from '../store/slices/statement-gl-transaction';
import { OrganizationType } from '../types';
import { StatementTransactionTypes } from './types';
import useStatementGLTransaction from './hooks/use-statement-gl-transaction';

import ROUTES from '../../../routes';

const GLTransactionHOC = ({
  customerType,
  accountsPage,
}: StatementTransactionTypes): React.JSX.Element => {
  const { customersTransactionTypes, baseCurrency } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const {
    transaction,
    debitAccountBalance,
    creditAccountBalance,
    transactionChangeLogsPagination,
    transactionChangeLogs,
  } = useAppSelector((state) => state.customersStatmenetsTransactions.gl);
  const { users } = useAppSelector((state) => state.administrationDictionaries);
  const { currencies: currenciesList, activePermissions } = useAppSelector(
    (state) => state.root,
  );

  const { transactionId, customerId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const {
    setPanelIsOpen,
    panelIsOpen,
    isLoading,
    isFetching,
    debitBalanceIsLoading,
    debitBalanceIsFetching,
    creditBalanceIsLoading,
    creditBalanceIsFetching,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getTransactionChangeLogsAction,
  } = useStatementGLTransaction({
    transaction,
    transactionId,
  });

  const linkedTransactionPath = useMemo(() => {
    const currentTransactionType = customersTransactionTypes?.find(
      (item) => item.id === transaction?.transaction?.typeId,
    );

    if (currentTransactionType) {
      let link = '#';

      if (
        currentTransactionType?.name === TransactionTypeEnum.INTERNAL &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_INTERNAL_LIST)
      ) {
        link = '/transactions/internal';
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.INCOMING &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_INCOMING_LIST)
      ) {
        link = '/transactions/incoming';
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.OUTGOING &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_OUTGOING_LIST)
      ) {
        link = '/transactions/outgoing';
      } else if (
        currentTransactionType?.name ===
          TransactionTypeEnum.CURRENCY_EXCHANGE &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_EXCHANGE_LIST)
      ) {
        link = '/transactions/exchange';
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.NOSTRO &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_NOSTRO_LIST)
      ) {
        link = '/transactions/nostro';
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.FEE &&
        activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_LIST,
        )
      ) {
        link = '/transactions/general-ledger/service-fee';
      }

      return `${link}/${transaction?.parentTransactionId}`;
    }

    return undefined;
  }, [
    customersTransactionTypes,
    transaction?.transaction?.typeId,
    transaction?.parentTransactionId,
  ]);

  const handleClose = () => {
    setPanelIsOpen(false);

    let link = '#';

    if (customerType === OrganizationType.INDIVIDUAL) {
      link = ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW_TRANSACTIONS;

      if (accountsPage) {
        link = ROUTES.CUSTOMERS_INDIVIDUAL_ACCOUNTS_TRANSACTIONS;
      }
    } else {
      link = ROUTES.CUSTOMERS_CORPORATE_OVERVIEW_TRANSACTIONS;

      if (accountsPage) {
        link = ROUTES.CUSTOMERS_CORPORATE_ACCOUNTS_TRANSACTIONS;
      }
    }

    setTimeout(() => {
      navigate(
        generatePath(`${link}${search || ''}`, {
          customerId: customerId as string,
        }),
      );
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearTransactionState());
    };
  }, []);

  return (
    <GeneralLedgerSidepanel
      panelIsOpen={panelIsOpen}
      handleClose={handleClose}
      transaction={transaction}
      isLoading={isLoading || isFetching}
      title="GL Transaction"
      linkedTransactionPath={
        linkedTransactionPath?.includes('#') ? undefined : linkedTransactionPath
      }
      debitAccountBalance={debitAccountBalance}
      creditAccountBalance={creditAccountBalance}
      debitBalanceIsLoading={debitBalanceIsLoading || debitBalanceIsFetching}
      creditBalanceIsLoading={creditBalanceIsLoading || creditBalanceIsFetching}
      currenciesList={currenciesList}
      baseCurrency={baseCurrency}
      sidepanelExpandedKey="gl-statement"
      activePermission={activePermissions}
      users={users}
      changeLogsIsLoading={changeLogsIsLoading}
      changeLogsIsFetching={changeLogsIsFetching}
      getTransactionChangeLogs={getTransactionChangeLogsAction}
      transactionChangeLogsPagination={transactionChangeLogsPagination}
      changeLogs={transactionChangeLogs}
    />
  );
};

export default GLTransactionHOC;
